/**
 *  Plan state module file
 **/

import { PRODUCTS_TYPES } from '@/constants'
import { getAppMetadata } from '@/plan/metadata/metadata'
import {
  isPackagagesFeatureEnabled,
  getDefaultSaveAsPublic,
} from '@/plan/metadata/metadata.helper'
import { Metadata, Package } from '@workspaces/types'

/* eslint-disable @typescript-eslint/no-explicit-any */
// The plan working-model is highly dynamic: server fields are merged in via
// spreads, `package` is nullable with a deep mutable filter tree, and `filters`
// are the untyped sub-plan filters. These `any`s mirror the original untyped JS
// and are a follow-up strict-typing target; scalar/structural fields are typed.
export interface AssetsModel {
  results: any[]
  meta: { stats?: { impressions?: number } } & Record<string, any>
  count: number
  page: number
  page_size: number
  loading: boolean
}

export interface PlanModel {
  loading: boolean
  dirty: boolean
  filters: any[]
  created_by: string | null
  count: number
  impressions: number
  legend: any
  package: any
  agency: string | null
  brand: string | null
  client: string | null
  basemap: any
  is_public: boolean
  originFeaturedPackage: any
  status?: Package.State
  // dynamic server/runtime fields (name, id, created, modified, editing, …)
  [key: string]: any
}

export interface PlanState {
  metadata: Metadata.AppMetadata
  plan: PlanModel
  activeSubPlanFiltersIndex: number
  plan_backup: PlanModel
  assets: AssetsModel
  assetsProperties: { allDigital: boolean; allStatic: boolean }
  mapUpdateFlags: { assets: boolean }
  countries: any[]
  packagesFlags: {
    open: { packages: boolean }
    selected: Record<string, any>
    halfSelected: Record<string, any>
    hidden: Record<string, any>
  }
  assetsFinishedLoad: boolean
  assetsDetails: any
  assetsReadyWithAllProperties: boolean
  isFeaturedPackageAdminDetail: boolean
  featuredPackageAdminDetailSourceTab: any
  highlightedAssets: any
  forceLogout: boolean
  packages: any[]
  plans: {
    subscribed: boolean
    user: any[]
    public: any[]
    comments: {
      latest_plans_updated: any[]
      subscribed: boolean
      data: any[]
      updatedAt: any
    }
    clients: Record<string, any>
  }
}

// metadata information
const appMetadata = getAppMetadata()
const allCountries = appMetadata.countries
const baseMaps = appMetadata.app_config.basemaps
const defaultSelectedCountries = appMetadata.countries
  .filter((country) =>
    appMetadata.plan_filters.widget_countries.default_selection_iso3.includes(
      country.iso3,
    ),
  )
  .map((country) => country.id)

export const MODEL_SUBPLAN_FILTERS = () => ({
  tabname: '',
  countries: defaultSelectedCountries,
  types: {
    [PRODUCTS_TYPES.L1_PANEL_CLASS]: [],
    [PRODUCTS_TYPES.L2_PRODUCT]: [],
    [PRODUCTS_TYPES.L3_SUBPRODUCT]: [],
    [PRODUCTS_TYPES.L4]: [],
    [PRODUCTS_TYPES.L5]: [],
    [PRODUCTS_TYPES.L6]: [],
    [PRODUCTS_TYPES.L7]: [],
    [PRODUCTS_TYPES.L8]: [],
    [PRODUCTS_TYPES.L9]: [],
  },
  [PRODUCTS_TYPES.IS_DIGITAL]: undefined,
  assets_excluded: [],
  assets_included: [],
  panels: { assetsIds: [] },
  panelsExcluded: { assetsIds: [] },
  package: null,
})

export const MODEL_PLAN_PACKAGE_PRICE = (
  price: number,
  production: number,
  assetAllocation: number,
  materialHandling: number,
  electricity: number,
  others: number,
) => ({
  grossPrice: price,
  discounts: {
    freePercentage: 0,
    mediaAgencyPercentage: 0,
    otherPercentage: 0,
  },
  fees: {
    production,
    assetAllocation,
    materialHandling,
    photography: 0,
    electricity,
    others,
  },
  totalNet:
    price +
    production +
    assetAllocation +
    materialHandling +
    electricity +
    others,
})

export const MODEL_PLAN_PACKAGE = () => {
  if (isPackagagesFeatureEnabled(appMetadata)) {
    return {
      state: Package.State.Draft,
      packages: undefined,
      // id: undefined,
      // name: undefined,
      price: null,
      assets: undefined,
      impressions: undefined,
      availability: undefined,
      filter: {
        priceRange: appMetadata.plan_filters.widget_packages.price_range,
        dateRange: [],
        panelType: {
          digital: undefined,
          l1: [],
          l2: [],
          l3: [],
          l4: [],
        },
      },
      bookedConfirmByAdminAt: undefined,
      bookedFinalizedAt: undefined,
      actions: [],
      featuredPackageProperties: null,
    }
  }

  return null
}

export const MODEL_LEGEND = () => ({
  productClass: 'l2product_id',
  hiddenProductsId: [],
  facingOrientationIcons: false,
  angleNorthMap: false,
  assetsIdsLayerVisibility: false,
  colorizeAssets: false,
})

export const MODEL_PLAN = () => ({
  loading: false,
  dirty: false,
  filters: [MODEL_SUBPLAN_FILTERS()],
  created_by: null,
  count: 0,
  impressions: 0,
  legend: MODEL_LEGEND(),
  package: MODEL_PLAN_PACKAGE(),
  agency: null,
  brand: null,
  client: null,
  basemap: baseMaps.default,
  is_public: getDefaultSaveAsPublic(appMetadata),
  originFeaturedPackage: null,
  ...(isPackagagesFeatureEnabled(appMetadata) && {
    status: Package.State.Draft,
  }),
})

export const MODEL_ASSETS = () => ({
  results: [],
  meta: {},
  count: 0,
  page: 1,
  page_size: 10,
  loading: false,
})

export const state = (): PlanState => ({
  metadata: appMetadata,
  plan: MODEL_PLAN(),
  activeSubPlanFiltersIndex: 0,
  plan_backup: MODEL_PLAN(),
  assets: MODEL_ASSETS(),
  assetsProperties: {
    allDigital: false,
    allStatic: false,
  },
  mapUpdateFlags: {
    assets: false,
  },
  countries: allCountries,
  packagesFlags: {
    open: {
      packages: true,
    },
    selected: {},
    halfSelected: {},
    hidden: {},
  },
  assetsFinishedLoad: false,
  assetsDetails: null,
  assetsReadyWithAllProperties: false,
  isFeaturedPackageAdminDetail: false,
  featuredPackageAdminDetailSourceTab: null,
  highlightedAssets: null,
  forceLogout: false,
  packages: [],
  plans: {
    subscribed: false,
    user: [],
    public: [],
    comments: {
      latest_plans_updated: [],
      subscribed: false,
      data: [],
      updatedAt: null,
    },
    clients: {},
  },
})
